import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  getStarredStatus,
  listStarredWorkers,
  starWorker,
  unstarWorker,
} from "../api/starredWorkerApi";
import { QueryKeys } from "../queryKeys";
import { useCustomQuery } from "../queryUtils";

export function useStarredWorkersQuery() {
  return useCustomQuery([QueryKeys.STARRED_WORKERS], listStarredWorkers, {
    staleTime: 30_000,
  });
}

export function useStarredStatusQuery(workerId: string | undefined) {
  return useCustomQuery(
    [QueryKeys.STARRED_WORKER_STATUS, workerId ?? ""],
    () => getStarredStatus(workerId as string),
    { enabled: !!workerId },
  );
}

export function useToggleStarredWorkerMutation(workerId: string) {
  const queryClient = useQueryClient();
  return useMutation<boolean, Error, boolean>({
    // input: nextStarred (true => star, false => unstar)
    mutationFn: async (nextStarred: boolean) => {
      if (nextStarred) return starWorker(workerId);
      return unstarWorker(workerId);
    },
    onMutate: async (nextStarred) => {
      await queryClient.cancelQueries({
        queryKey: [QueryKeys.STARRED_WORKER_STATUS, workerId],
      });
      const previous = queryClient.getQueryData<boolean>([
        QueryKeys.STARRED_WORKER_STATUS,
        workerId,
      ]);
      queryClient.setQueryData(
        [QueryKeys.STARRED_WORKER_STATUS, workerId],
        nextStarred,
      );
      return { previous };
    },
    onError: (_err, _next, ctx) => {
      const previous = (ctx as { previous?: boolean } | undefined)?.previous;
      if (typeof previous === "boolean") {
        queryClient.setQueryData(
          [QueryKeys.STARRED_WORKER_STATUS, workerId],
          previous,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.STARRED_WORKER_STATUS, workerId],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.STARRED_WORKERS] });
    },
  });
}
